import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TotalsProgressBar } from '../ActivityTotals/TotalsProgressBar/TotalsProgressBar';
import CompactPageOwnerAvatarStory from '../CompactPageOwnerStory/CompactPageOwnerAvatarStory';
import TruncatedPageOwnerTitle from '../PageOwnerTitle/PageTitle';
import styles from './InstagramShare.scss';
import { PAGES_UI_FEATURE_IMAGE } from '../../constants/imageTemplateStrings';
import { JustGivingLogo } from '../JustGivingLogo/JustGivingLogo';
import classNames from 'classnames';
import SVG from '@justgiving/svg';
import { successIcon } from '@justgiving/icons';
import { useShareContext } from '../../context/ShareContext';

export enum IG_SHARE_TYPE {
  POST,
  STORY,
}

interface InstagramShareProps {
  heroImageUrl: string;
  altText: string;
  type: IG_SHARE_TYPE;
  copyText: string;
}

export const InstagramShare: React.FunctionComponent<InstagramShareProps> = ({
  heroImageUrl,
  altText,
  type,
  copyText,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const [copyState, setCopyState] = useState('');
  const [animated, setAnimated] = useState(true);
  const { shareItems } = useShareContext();

  const isStory = type === IG_SHARE_TYPE.STORY;
  const isCopySuccess = copyState === 'success';

  async function copy() {
    const urlWithUtm = copyText.replace(
      shareItems.instagramShareModal.url,
      shareItems.instagramShareModal.utmUrl,
    );
    try {
      await navigator.clipboard.writeText(urlWithUtm);
      setCopyState('success');
    } catch {
      setCopyState('error');
    }
  }

  useEffect(() => {
    setAnimated(true);

    copy();
  }, [copyText]);

  return (
    <div
      className={animated ? styles.animation : ''}
      onAnimationEnd={() => {
        setAnimated(false);
      }}
    >
      <div className={isStory ? styles.storyContainer : ''}>
        <div
          className={classNames(
            styles.shareContainer,
            styles.boxShadow,
            isStory
              ? styles.shareContainer__story
              : styles.shareContainer__post,
          )}
        >
          <div className={isStory ? styles.imageAndLogo : ''}>
            {isStory && (
              <div className={styles.logo}>
                <JustGivingLogo />
              </div>
            )}
            <div className={styles.imageContainer__story}>
              <img
                src={`${heroImageUrl}?template=${PAGES_UI_FEATURE_IMAGE}`}
                alt={altText}
                className={classNames(
                  styles.shareModalImage,
                  !isStory && styles.shareModalImage__post,
                )}
              />
            </div>
          </div>
          <div className={styles.shareTextAndTotals}>
            <TruncatedPageOwnerTitle truncateAt={60} />
            <CompactPageOwnerAvatarStory linkToFrp={false} />
            <TotalsProgressBar isInstagramShare={true} />
          </div>
        </div>
      </div>
      <div
        className={
          isStory ? styles.captionContainer__story : styles.captionContainer
        }
      >
        <>
          <p className={styles.captionContainerTitle}>
            {isStory
              ? t('shareModal.instagram.shareLinkCtaStory')
              : t('shareModal.instagram.shareLinkCtaPost')}
          </p>
          <div
            className={
              isStory
                ? styles.innerCaptionCopy__story
                : styles.innerCaptionCopy__post
            }
          >
            {isStory ? (
              <>
                <span>{copyText}</span>
              </>
            ) : (
              <>
                <p>{copyText}</p>
              </>
            )}
            {!isCopySuccess && (
              <button className={styles.copyButton} onClick={copy}>
                {isStory
                  ? t('common.copyLink')
                  : t('shareModal.instagram.copyCaption')}
              </button>
            )}
          </div>
          {}
          {isCopySuccess && (
            <div className={styles.copyToastSuccess}>
              <SVG markup={successIcon} className={styles.icon} />
              <div className={styles.message}>
                {t('shareModal.instagram.copyLink')}{' '}
                <button className={styles.urlCopy} onClick={copy}>
                  {t('shareModal.instagram.copyAgain')}
                </button>
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  );
};
